import { Box } from "@chakra-ui/layout";
import {
  Background,
  BackgroundVariant,
  Controls,
  DefaultEdgeOptions,
  MarkerType,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from "@xyflow/react";
import { edgeTypes } from "features/entities/EntityCreatePage/EntityGraph/edges";
import { GrayScaleProvider } from "features/entities/EntityCreatePage/EntityGraph/GrayScaleContext";
import GrayScaleToggle from "features/entities/EntityCreatePage/EntityGraph/GrayScaleToggle";
import { useFormSync } from "features/entities/EntityCreatePage/EntityGraph/hooks/useFormSync";
import { useGraphInitLayout } from "features/entities/EntityCreatePage/EntityGraph/hooks/useGraphLayout";
import { useGraphValidation } from "features/entities/EntityCreatePage/EntityGraph/hooks/useGraphValidation";
import { useImperativeGraphRef } from "features/entities/EntityCreatePage/EntityGraph/hooks/useImperativeGraphRef";
import { useInitialGraphNodesEdges } from "features/entities/EntityCreatePage/EntityGraph/hooks/useInitialGraphNodesEdges";
import { useNudge } from "features/entities/EntityCreatePage/EntityGraph/hooks/useNudge";
import { nodeTypes } from "features/entities/EntityCreatePage/EntityGraph/nodes";
import { GraphRef } from "features/entities/EntityCreatePage/EntityGraph/types";
import { forwardRef } from "react";

export type Props = {
  fundFamilyId: string;
};

const defaultEdgeOptions: DefaultEdgeOptions = {
  style: { strokeWidth: 4 },
  labelStyle: { fontSize: 18, fontWeight: "bold" },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 10,
    height: 10,
  },
};

const EntityGraph = forwardRef<GraphRef, Props>(({ fundFamilyId }, ref) => {
  const { initalNodes, initialEdges } = useInitialGraphNodesEdges(fundFamilyId);

  const [nodes, setNodes, onNodesChange] = useNodesState(initalNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useGraphInitLayout(nodes, edges, setEdges, setNodes);
  useImperativeGraphRef(ref);

  const edgeFormSyncProps = useFormSync();
  const graphValidationProps = useGraphValidation(onNodesChange, onEdgesChange);
  // intentionally only show nudge when needed with initial nodes and edges
  const nudgeUsersToClosingWorkflow = useNudge(initalNodes, initialEdges);

  return (
    <GrayScaleProvider>
      <Box height="100%">
        <ReactFlow
          nodes={nodes}
          nodeTypes={nodeTypes}
          edges={edges}
          edgeTypes={edgeTypes}
          fitView={true}
          defaultEdgeOptions={defaultEdgeOptions}
          {...edgeFormSyncProps}
          {...graphValidationProps}
        >
          <GrayScaleToggle />
          <Background variant={BackgroundVariant.Lines} bgColor="white" />
          <Controls />
          {nudgeUsersToClosingWorkflow}
        </ReactFlow>
      </Box>
    </GrayScaleProvider>
  );
});

EntityGraph.displayName = "EntityGraph";

export default EntityGraph;
